import { ReactNode } from "react";
import { Box, Divider, Paper, Stack, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AssignedBillingInfo, ClientBillingCustomer } from "../../types/billing";
import { BILLING_ENTITIES, formatPaymentMethod } from "../../utils/billingFormat";
import { formatCurrency } from "../../utils/formatCurrency";

export function BillingField({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" component="div">{label}</Typography>
      <Box sx={{ typography: "body2", fontWeight: 600, overflowWrap: "anywhere" }}>{children}</Box>
    </Box>
  );
}

export function BillingBalanceField({ customer }: { customer: ClientBillingCustomer }) {
  const { balance, currency } = customer;
  if (balance === 0 || !currency) return null;

  return (
    <BillingField
      label={
        <>
          {balance < 0 ? "Credit balance" : "Balance due"}
          <Tooltip
            arrow
            title={balance < 0 ? "Applied automatically to reduce your next invoice." : "Added automatically to your next invoice."}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: "text-bottom", cursor: "help" }} />
          </Tooltip>
        </>
      }
    >
      <Box component="span" sx={{ fontWeight: 600, color: balance < 0 ? "success.main" : "text.primary" }}>
        {formatCurrency(Math.abs(balance), currency)}
      </Box>
    </BillingField>
  );
}

interface Props {
  info: AssignedBillingInfo;
  details?: ReactNode;
  actions?: ReactNode;
}

export default function BillingCustomerPane({ info, details, actions }: Props) {
  const entity = BILLING_ENTITIES[info.region];

  return (
    <Paper variant="outlined" component="section" aria-label="Customer" sx={{ p: 2.5 }}>
      <Typography variant="overline" color="text.secondary">Customer</Typography>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <BillingField label="Name">
          {info.customer.name || "-"}
          <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: "text.secondary" }}>{info.customer.id}</Typography>
        </BillingField>
        <BillingField label="Email">{info.customer.email || "-"}</BillingField>
        <BillingField label="Default payment method">
          {info.defaultPaymentMethod
            ? formatPaymentMethod(info.defaultPaymentMethod)
            : "No default payment method set."}
        </BillingField>
        <BillingBalanceField customer={info.customer} />
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Typography variant="overline" color="text.secondary">Billing</Typography>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <BillingField label="Billing entity">{`${entity.name} · ${entity.currency}`}</BillingField>
        {details}
      </Stack>
      {actions && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>{actions}</Stack>
        </>
      )}
    </Paper>
  );
}
